import { useState, useEffect, useRef } from "react";

/**
 * Optimise une URL d'image : si c'est Pexels ou Unsplash, on demande
 * une version compressée et redimensionnée, beaucoup plus légère.
 * Pour les data:image (base64) et autres URLs, on retourne tel quel.
 */
function optimizeUrl(url, width = 1600) {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("data:")) return url;

  try {
    const u = new URL(url);
    const host = u.hostname;
    if (host.includes("pexels.com")) {
      u.searchParams.set("auto", "compress");
      u.searchParams.set("cs", "tinysrgb");
      u.searchParams.set("w", String(width));
      u.searchParams.set("q", "75");
      return u.toString();
    }
    if (host.includes("unsplash.com")) {
      u.searchParams.set("auto", "format,compress");
      u.searchParams.set("w", String(width));
      u.searchParams.set("q", "75");
      u.searchParams.set("fm", "jpg");
      return u.toString();
    }
    return url;
  } catch {
    return url;
  }
}

/**
 * Image avec placeholder couleur instantané + fondu doux à l'arrivée.
 * Précharge dans un objet Image() en mémoire pour éviter le "pop in" tardif.
 */
export default function SmartImage({
  src,
  alt = "",
  className = "",
  width: w = 1600,
  eager = false,
  ...rest
}) {
  const optimized = optimizeUrl(src, w);
  const [loaded, setLoaded] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    setLoaded(false);
    if (!optimized) return;
    const img = new Image();
    img.decoding = "async";
    img.src = optimized;
    const done = () => mountedRef.current && setLoaded(true);
    if (img.complete && img.naturalWidth > 0) done();
    else {
      img.onload = done;
      img.onerror = done;
    }
    return () => { mountedRef.current = false; };
  }, [optimized]);

  if (!optimized) {
    return <div className={`bg-forest/10 ${className}`} aria-hidden="true" />;
  }

  return (
    <div className={`relative overflow-hidden bg-forest/10 ${className}`}>
      <img
        src={optimized}
        alt={alt}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        {...(eager ? { fetchpriority: "high" } : {})}
        onLoad={() => setLoaded(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
        {...rest}
      />
    </div>
  );
}
